import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/Layout';

interface ActivityLog {
  id: number;
  admin_id: number | null;
  team_member_id: number | null;
  client_id: number | null;
  action_type: string;
  action_detail: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  admin_username: string | null;
  team_member_name: string | null;
  client_name: string | null;
}

export function AdminLogs() {
  const [admin, setAdmin] = useState<any>(null);
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  const getApiBase = () => {
    return '/api';
  };

  const request = async (endpoint: string, options: RequestInit = {}) => {
    const response = await fetch(`${getApiBase()}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...options?.headers },
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Request failed');
    return data;
  };

  useEffect(() => {
    const stored = localStorage.getItem('rewagent_admin');
    if (stored) {
      setAdmin(JSON.parse(stored));
    } else {
      navigate('/admin');
    }
  }, [navigate]);

  useEffect(() => {
    if (admin) fetchLogs();
  }, [admin, page]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const data = await request(`/admin/logs?page=${page}&limit=50`);
      setLogs(data.logs || []);
      setTotal(data.pagination?.total || 0);
      setTotalPages(data.pagination?.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    }
    finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try { await request('/auth/logout', { method: 'POST' }); } catch { }
    localStorage.removeItem('rewagent_admin');
    localStorage.removeItem('rewagent_token');
    navigate('/admin');
  };

  if (!admin) return null;

  return (
    <AdminLayout
      activeTab="logs"
      onTabChange={(tab) => {
        const baseTab = tab.split('?')[0];
        navigate(`/admin/${['leads', 'access-codes', 'campaigns', 'logs'].includes(baseTab) ? tab : 'dashboard'}`);
      }}
      onLogout={handleLogout}
    >
      <div className="p-10 max-w-7xl mx-auto">
        <div className="mb-10 flex items-center gap-4">
          <button onClick={() => navigate('/admin/dashboard')} className="p-2 border border-[#e3dbcf] rounded-lg hover:bg-[#e3dbcf]/30 text-[#6b635a] transition-all">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h1 className="text-4xl font-bold text-[#1a1a1a] mb-2 tracking-tight">System Audit Logs</h1>
            <p className="text-[#6b635a] text-sm uppercase tracking-widest font-semibold">Track administrative actions</p>
          </div>
        </div>

        <div className="mb-5 text-sm font-medium text-[#8c8578] uppercase tracking-widest">
          {total === 0 ? '0 EVENTS' : `${logs.length} OF ${total} EVENTS`}
        </div>

        <div className="bg-[#fcfbfa] border border-[#e3dbcf] rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#e3dbcf]">
                  <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">Date / Time</th>
                  <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">User</th>
                  <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">Action</th>
                  <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">Client</th>
                  <th className="px-6 py-5 text-left text-xs font-semibold text-[#8c8578] uppercase tracking-widest">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e3dbcf]">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="py-12 text-center text-[#8c8578]">Loading logs...</td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-12 text-center text-[#8c8578]">No audit logs found</td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="hover:bg-[#f3ede3]/50 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-[#6b635a] whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-[#1a1a1a]">
                        {log.admin_username || log.team_member_name || (log.admin_id ? `Admin #${log.admin_id}` : 'System')}
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex px-2.5 py-1 rounded text-xs font-semibold tracking-wide border bg-[#1a1a1a] text-[#fcfbfa] border-[#1a1a1a]">
                          {(log.action_type || 'unknown').toUpperCase().replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#1a1a1a] font-medium">
                        {log.client_name || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#6b635a] max-w-md truncate" title={log.action_detail || ''}>
                        {log.action_detail || '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {!loading && totalPages > 1 && (
            <div className="p-6 border-t border-[#e3dbcf] flex items-center justify-between">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-5 py-2.5 bg-[#fcfbfa] hover:bg-[#1a1a1a] text-[#1a1a1a] hover:text-[#fcfbfa] border border-[#e3dbcf] rounded-lg text-sm font-medium disabled:opacity-40 transition-all"
              >
                Previous
              </button>
              <span className="text-[#8c8578] text-sm font-medium uppercase tracking-widest">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-5 py-2.5 bg-[#fcfbfa] hover:bg-[#1a1a1a] text-[#1a1a1a] hover:text-[#fcfbfa] border border-[#e3dbcf] rounded-lg text-sm font-medium disabled:opacity-40 transition-all"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
